import { Link } from "react-router-dom";
import Logo from "@/assets/logo.png";

const Footer: React.FC = () => {
	const currentYear = new Date().getFullYear();

	return (
		<footer className="bg-black text-white py-16 border-t border-white/10 w-full overflow-hidden">
			<div className="w-full px-6 md:px-12 lg:px-24">
				<div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 items-center">
					{/* Logo Section - Centered */}
					<div className="flex flex-col items-center space-y-6">
						<Link to="/" className="inline-block group">
							<img
								src={Logo}
								alt="WILDFÖRSTER"
								className="h-16 md:h-20 w-auto grayscale brightness-200 opacity-70 group-hover:opacity-100 transition-opacity duration-500"
							/>
						</Link>
					</div>

					{/* Navigation & Legal Links - Centered */}
					<div className="flex flex-col items-center space-y-6 md:border-x md:border-white/5 py-2">
						<p className="text-[#cfc297] text-[10px] uppercase tracking-[0.5em] font-black">
							Navigation
						</p>
						<nav className="flex flex-col items-center space-y-4 text-[11px] md:text-xs uppercase tracking-[0.2em] font-bold">
							<Link
								to="/impressum"
								className="text-white/50 hover:text-white transition-all duration-300"
							>
								Impressum
							</Link>
							<Link
								to="/datenschutz"
								className="text-white/50 hover:text-white transition-all duration-300"
							>
								Datenschutz
							</Link>
							<button
								onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
								className="text-[#cfc297]/60 hover:text-[#cfc297] transition-all duration-300 text-center uppercase"
							>
								Nach Oben ↑
							</button>
						</nav>
					</div>

					{/* Social Connect - Centered */}
					<div className="flex flex-col items-center space-y-6">
						<p className="text-[#cfc297] text-[10px] uppercase tracking-[0.5em] font-black text-center">
							Folge Uns
						</p>
						<div className="flex items-center justify-center gap-4">
							<SocialLink
								href="https://m.facebook.com/Daniel-Wildf%C3%B6rster-113290246971839/"
								label="Facebook"
							>
								<div className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center hover:bg-white hover:text-black transition-all duration-500">
									<FacebookIcon />
								</div>
							</SocialLink>
							<SocialLink
								href="https://www.instagram.com/wildforsterdaniel/?hl=de"
								label="Instagram"
							>
								<div className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center hover:bg-white hover:text-black transition-all duration-500">
									<InstagramIcon />
								</div>
							</SocialLink>
							<SocialLink
								href="https://www.tiktok.com/@wildfoerster13?"
								label="TikTok"
							>
								<div className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center hover:bg-white hover:text-black transition-all duration-500">
									<TikTokIcon />
								</div>
							</SocialLink>
						</div>
					</div>
				</div>

				{/* Global Copyright Bar */}
				<div className="mt-20 pt-10 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-6 text-[9px] uppercase tracking-[0.3em] text-white/20">
					<p className="font-medium">
						&copy; {currentYear} WILDFÖRSTER Fahrzeugaufbereitung
					</p>
					<div className="h-px w-8 bg-white/10 hidden md:block" />
					<p className="font-light text-center lowercase">
						mit <span className="text-red-500">♥️</span> programmiert von{" "}
						<a
							href="https://examplesart.de"
							target="_blank"
							rel="noopener noreferrer"
							className="text-white/40 hover:text-[#cfc297] transition-colors"
						>
							examplesart
						</a>
					</p>
				</div>
			</div>
		</footer>
	);
};

interface SocialLinkProps {
	readonly href: string;
	readonly label: string;
	readonly children: React.ReactNode;
}

const SocialLink: React.FC<SocialLinkProps> = ({ href, label, children }) => (
	<a
		href={href}
		target="_blank"
		rel="noopener noreferrer"
		className="text-white/70 hover:text-[#cfc297] transition-colors"
		aria-label={label}
	>
		{children}
	</a>
);

const FacebookIcon: React.FC = () => (
	<svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
		<path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
	</svg>
);

const InstagramIcon: React.FC = () => (
	<svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
		<path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
	</svg>
);

const TikTokIcon: React.FC = () => (
	<svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
		<path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
	</svg>
);

export default Footer;
